import { useTheme } from "@mui/material";
import { Bookmark } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Header } from "./Header.component";
import {
  ActionsRow,
  DescriptionText,
  EmptyStateText,
  GenericContentContainer,
  StartActionsGroup
} from "./LearningActivity.styles";
import { LearningActivityContentSkeleton } from "./LearningActivityContentSkeleton.component";
import { UtilityTabs } from "./UtiliyTabs.component";
import { ctaDisabled } from "./utils";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { CardCta } from "../../components/CardCta/CardCta.component";
import { CtaOverlay } from "../../components/CtaOverlay/CtaOverlay.component";
import {
  useFavouritesMutation,
  useLearningObjectQuery,
  useRatingQuery,
  useToolsQuery
} from "../../queries";
import { useAuthStore } from "../../stores";
import type {
  LearningActivity,
  ReferencedLearningActivity
} from "../../types";
import { getError } from "../../utils/error";
import { getLearningObjectType } from "../../utils/general";

type ILearningActivityGenericContent = {
  activity: ReferencedLearningActivity
  handleClose: (e: React.MouseEvent<HTMLButtonElement>) => void,
}

export const LearningActivityGenericContent = ({
  activity,
  handleClose
}: ILearningActivityGenericContent) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const funcFavourites = useAuthStore(state => state.smartConfiguration?.funcFavourites);

  const { id, courseId, learningObjectTypology, rootId } = activity;

  const {
    data: fullLO,
    error: fullLOError,
    isFetching: fullLOIsFetching
  } = useLearningObjectQuery({
    rootId: rootId,
    courseId: courseId,
    id: id ?? "",
    learningObjectTypology: learningObjectTypology
  });

  const { data: tools, isFetching: toolsIsFetching } = useToolsQuery({
    learningObjectId: fullLO?.id?.toString() ?? ""
  });

  const fullActivity = fullLO as LearningActivity | undefined;

  const { data: ratingStatistic } = useRatingQuery({
    enabled: Boolean(activity.id),
    learningObjectId: activity.id,
    learningObjectTypology: getLearningObjectType(activity.learningObjectTypology)
  });

  const isFavourite = ratingStatistic
    ? Boolean(ratingStatistic.userRating.isFavouriteForLearner
      && ratingStatistic.objectStatistics?.learningObjectId)
    : Boolean(fullActivity?.isFavourite);

  const favParentId = fullActivity?.isStandAlone
    ? null
    : (!courseId && rootId) ? rootId : courseId;
  const favGrandParentId = fullActivity?.isStandAlone
    ? null
    : (favParentId && favParentId !== rootId ? rootId : null);

  const { mutate: addFavourite, isPending: isAddingFav } = useFavouritesMutation({
    grandParentId: favGrandParentId,
    learningObjectId: activity.id,
    learningObjectTypology: activity.learningObjectTypology,
    method: "POST",
    parentId: favParentId
  });
  const { mutate: removeFavourite, isPending: isRemovingFav } = useFavouritesMutation({
    grandParentId: favGrandParentId,
    learningObjectId: activity.id,
    learningObjectTypology: activity.learningObjectTypology,
    method: "DELETE",
    parentId: favParentId
  });

  if (!fullActivity) {
    if (fullLOIsFetching) {
      return (
        <GenericContentContainer>
          <Header
            activity={ { ...activity } as unknown as LearningActivity }
            handleClose={ handleClose }
            hideStatusBoxes
          />
          <LearningActivityContentSkeleton />
        </GenericContentContainer>
      );
    }
    const errorMessage = getError(fullLOError)?.[0]?.message
      ?? t("no_data_available");
    return (
      <GenericContentContainer>
        <Header
          activity={ { ...activity } as unknown as LearningActivity }
          handleClose={ handleClose }
          hideStatusBoxes
        />
        <EmptyStateText role="alert">{ errorMessage }</EmptyStateText>
      </GenericContentContainer>
    );
  }

  if (toolsIsFetching) {
    return (
      <GenericContentContainer>
        <Header activity={ fullActivity } handleClose={ handleClose } />
        <LearningActivityContentSkeleton />
      </GenericContentContainer>
    );
  }

  const description = fullActivity.longDescription || fullActivity.shortDescription || "";
  const percentageOfCompletion = fullActivity.percentageOfCompletion ?? "0%";
  const rating = ratingStatistic?.objectStatistics?.averageRating
    ?? fullActivity.averageRatingScore
    ?? 0;
  const isFavouriteLoading = isAddingFav || isRemovingFav;

  const handleToggleFavourite = () => {
    if (isFavourite) {
      removeFavourite();
    } else {
      addFavourite();
    }
  };

  return (
    <GenericContentContainer>
      <Header activity={ fullActivity } handleClose={ handleClose } />

      { description ? (
        <DescriptionText>{ description }</DescriptionText>
      ) : null }

      <ActionsRow>
        <StartActionsGroup role="group" aria-label={ t("actions", "Azioni") }>
          <CardCta
            canAccessNow={ fullActivity.canAccessNow ?? true }
            courseId={ courseId }
            credits={ fullActivity.credits ?? 0 }
            ecmRegistration={ fullActivity.ecmRegistration }
            ecm_specialization={ fullActivity.ecm_specialization }
            enrollType={ fullActivity.enrollType }
            id={ fullActivity.id }
            isEditorial={ false }
            isFavourite={ isFavourite }
            isLinkRelative={ fullActivity.isLinkRelative }
            isStandAlone={ fullActivity.isStandAlone }
            learningObjectType={ fullActivity.learningObjectType }
            learningObjectTypology={ fullActivity.learningObjectTypology }
            link={ fullActivity.link }
            lo_Tag={ fullActivity.lo_Tag }
            percentageOfCompletion={ percentageOfCompletion }
            rootId={ rootId }
            simple
            status={ fullActivity.status }
            disabled={ ctaDisabled(fullActivity) }
          />
          { funcFavourites && fullActivity.isStandAlone && !courseId && !rootId ? (
            <ButtonDigited
              aria-label={ t(isFavourite ? "remove_from_favourites" : "add_to_favourites") }
              aria-pressed={ isFavourite }
              typology="icononly"
              buttonSize="md"
              disabled={ isFavouriteLoading }
              onClick={ handleToggleFavourite }
            >
              <Bookmark
                aria-hidden="true"
                fill={ isFavourite ? theme.customColors.textPrimary : "transparent" }
                stroke={ theme.customColors.textPrimary }
              />
            </ButtonDigited>
          ) : null }
        </StartActionsGroup>
        <CtaOverlay
          iconOnly
          data={ {
            courseId: courseId ?? undefined,
            id: fullActivity.id,
            isFavourite,
            isStandAlone: fullActivity.isStandAlone,
            learningObjectTypology: fullActivity.learningObjectTypology,
            rating,
            rootId: rootId ?? undefined
          } }
          keepIconsInMobile={ true }
          certificates={ fullActivity.certificates }
          hideRating
          sxContainer={ {
            [theme.breakpoints.down("sm")]: {
              position: "relative"
            }
          } }
        />
      </ActionsRow>
      { tools?.length ? (
        <UtilityTabs
          learningObject={ fullLO as LearningActivity }
          tools={ tools ?? [] }
          exercises={ [] }
        />
      ) : null }
    </GenericContentContainer>
  );
};
